/**
 * Ensures the Noto Sans Gujarati TTF (bundled in /public/fonts) is loaded
 * before any html2pdf.js rasterization runs. html2pdf.js works by
 * screenshotting the DOM through html2canvas — if the font isn't loaded
 * yet, browsers substitute a fallback and Gujarati glyphs come out as
 * dots / tofu. Call `await ensureGujaratiFontReady()` immediately before
 * any html2pdf().from(...).save() call.
 *
 * Idempotent: the @font-face tag is injected only once per page load.
 * Returns a Promise that resolves once the browser reports the font
 * available (or after a short timeout, so a slow/blocked font never
 * blocks PDF download indefinitely).
 */
let _injected = false;

const FONT_CSS = `
@font-face {
  font-family: 'Noto Sans Gujarati';
  src: url('/fonts/NotoSansGujarati-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Noto Sans Gujarati';
  src: url('/fonts/NotoSansGujarati-SemiBold.ttf') format('truetype');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Noto Sans Gujarati';
  src: url('/fonts/NotoSansGujarati-Bold.ttf') format('truetype');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}
.text-gujarati, .noto-guj { font-family: 'Noto Sans Gujarati', 'Inter', Arial, sans-serif !important; }
`;

export async function ensureGujaratiFontReady(timeoutMs = 3500) {
  if (typeof document === 'undefined') return;
  if (!_injected) {
    const style = document.createElement('style');
    style.setAttribute('data-guj-font', 'true');
    style.appendChild(document.createTextNode(FONT_CSS));
    document.head.appendChild(style);
    _injected = true;
  }
  // Kick the loader explicitly for both weights.
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load('400 14px "Noto Sans Gujarati"', 'કાગજબુક'),
        document.fonts.load('700 14px "Noto Sans Gujarati"', 'કાગજબુક'),
      ]).then(() => document.fonts.ready),
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  } catch (_e) {
    // No-op — we still let html2pdf try.
  }
}
